import { getLogger } from '@logtape/logtape';
import { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { z } from 'zod';

import CollapsableItem from '@/components/CollapsableItem.tsx';
import CollapsableSection from '@/components/CollapsableSection.tsx';
import PatientInformation from '@/components/PatientModal/PatientInformation.tsx';
import { PatientModalAppointments } from '@/components/PatientModal/patient-modal-appointments.tsx';
import { PatientModalHeader } from '@/components/PatientModal/patient-modal-header.tsx';
import { PatientTasks } from '@/components/PatientModal/patient-tasks.tsx';

import { revalidateAppointmentsRangeCache } from '@/hooks/use-appointments-for-date-range.ts';
import { useCancelAppointment } from '@/hooks/use-cancel-appointment.ts';
import useAppointmentsForDate from '@/hooks/useAppointmentsForDate.ts';
import useConfig from '@/hooks/useConfig.ts';
import { useEnrichedPractitionerDataForSpecificInstance } from '@/hooks/useEnrichedPractitionerData.ts';
import usePatient from '@/hooks/usePatient.ts';

import { CardStatus } from '@/models/CardStatus.ts';
import { Appointment, CollectionActionScope, DailyAppointment, Priority } from '@/models/appointment/Appointment.ts';
import { CancelAppointmentOptions } from '@/models/appointment/CancelAppointmentOptions.ts';
import { PVSProvider } from '@/models/customers/PVSProvider.ts';
import { InsuranceType } from '@/models/insurance.ts';

import { getAppointmentById, getPatientAppointmentsByDay, updateAppointment } from '@/services/appointmentService.ts';
import { showErrorNotification } from '@/services/notificationService.ts';

import { usePatientModalStore } from '@/stores/patientModalStore.ts';

import { buildChainFutureCountMap, isEternoAppointment } from '@/utils/appointmentUtils.ts';
import { getInstanceConfig } from '@/utils/configUtils.ts';
import { getInsuranceTypeFromAppointment } from '@/utils/insurance-utils.ts';
import { openAppointmentScheduler } from '@/utils/modal/appointmentSchedulerUtils.tsx';
import { openCardReadModal, openCollectionEditScopeModal } from '@/utils/modalUtils.tsx';
import { enrichAppointmentWithPatient, getPatientAppointments } from '@/utils/patientUtils.ts';

import CircularLoader from '../CircularLoader.tsx';
import ImportanceSelector from '../ImportanceSelector.tsx';
import { AppointmentDetails } from './appointment-details.tsx';
import DocumentExchange from './document-exchange.tsx';

const logger = getLogger(['component', 'patient-modal']);

interface Props {
    pvsPatientId: string;
    eternoPatientId: string | undefined;
    instanceId: string;
}

export const PatientModal = ({ pvsPatientId, eternoPatientId, instanceId }: Props) => {
    const { appointment, is_importance_opened: isImportanceOpened } = usePatientModalStore();
    const [appointmentLoadingOverlay, setAppointmentLoadingOverlay] = useState<boolean>(false);
    const { location_key: locationKey, customer_id: customerId } = useConfig();
    const config = getInstanceConfig(instanceId);

    const isCardReadEnabled = config?.pvs_provider === PVSProvider.DOC_CIRRUS;
    // Tasks are a Doc Cirrus feature
    const isTasksEnabled = config?.pvs_provider === PVSProvider.DOC_CIRRUS;

    // Create a ref for the modal content
    const modalContentRef = useRef<HTMLDivElement>(null);
    const { patient } = usePatient({
        user_id: pvsPatientId,
        instance_id: instanceId,
        pvs_provider: config?.pvs_provider,
        eterno_user_id: eternoPatientId,
    });

    // Enrich appointment with patient data when both are available
    const enrichedAppointment: DailyAppointment | undefined = useMemo(() => {
        if (!appointment || !patient) return appointment;
        return enrichAppointmentWithPatient(appointment, patient);
    }, [appointment, patient]);

    const { mutate: mutateAppointmentsForDate } = useAppointmentsForDate({
        show_cancelled: false,
        show_cancelled_today: true,
    });

    // Necessary for Editing
    const { data: enrichedPractitionerData } = useEnrichedPractitionerDataForSpecificInstance(instanceId);

    const {
        data: patientAppointments,
        isLoading: isLoadingPatientAppointments,
        mutate: mutatePatientAppointments,
    } = useSWR([eternoPatientId, pvsPatientId], () => {
        return getPatientAppointments(pvsPatientId, eternoPatientId, instanceId);
    });

    const chainFutureCountMap = buildChainFutureCountMap(patientAppointments?.future);

    const getInsuranceCardReadStatus = (): CardStatus | undefined => {
        if (!appointment) return undefined;

        const insuranceTypeResult = z.enum(InsuranceType).safeParse(getInsuranceTypeFromAppointment(appointment));
        if (!insuranceTypeResult.success) return undefined;

        switch (insuranceTypeResult.data) {
            case InsuranceType.SELF_PAYER: {
                return undefined;
            }
            case InsuranceType.PRIVATE: {
                return CardStatus.NOT_APPLICABLE;
            }
            case InsuranceType.PUBLIC: {
                return appointment.data.is_insurance_card_active ? CardStatus.READ : CardStatus.UNREAD;
            }
            default: {
                return CardStatus.NOT_APPLICABLE;
            }
        }
    };

    const insuranceCardReadStatus = getInsuranceCardReadStatus();

    const refreshAppointment = async (apt: Appointment): Promise<void> => {
        const isEternoApp = isEternoAppointment(apt);
        const pvsUserId = patient?.pvs_user_id ?? '';
        const instanceId = apt.links?.dc_instance_id ?? apt.data_cur_links?.instance_id ?? '';

        try {
            let updatedApt: DailyAppointment | undefined;
            if (isEternoApp) {
                updatedApt = await getAppointmentById(apt.id, apt.user_id);
            } else {
                const updatedAptList = await getPatientAppointmentsByDay(
                    pvsUserId,
                    apt.pvs_provider,
                    instanceId,
                    apt.day,
                    locationKey
                );
                const refreshedAppointment = updatedAptList.find((updatedApt) => updatedApt.id === apt.id);
                updatedApt = { ...apt, ...refreshedAppointment };
            }
            if (updatedApt) {
                usePatientModalStore.setState({
                    appointment: updatedApt,
                });
            }
        } catch (error) {
            logger.error('Error while refreshing appointment', { error: error });
        }
    };

    const handleEdit = (apt: Appointment): void => {
        const openScheduler = (isSingleCollectionEdit?: boolean, editingCollectionId?: string): void => {
            openAppointmentScheduler({
                mode: 'edit',
                existing_appointment: apt,
                patient: patient,
                enriched_practitioner_data: enrichedPractitionerData,
                isSingleCollectionEdit: isSingleCollectionEdit,
                editingCollectionId: editingCollectionId,
                onClose: async () => {
                    await refreshAppointment(apt);
                },
            });
        };

        if (apt.collection_id) {
            openCollectionEditScopeModal(
                (scope) => {
                    if (scope === CollectionActionScope.SINGLE) {
                        openScheduler(true);
                    } else {
                        openScheduler(undefined, apt.collection_id);
                    }
                },
                apt,
                patient?.instance_id
            );
            return;
        }

        openScheduler();
    };

    const savePriority = (new_priority: Priority): void => {
        // Update the appointment in the store with new priority
        usePatientModalStore.setState((state) => ({
            appointment: state.appointment ? { ...state.appointment, priority: new_priority } : undefined,
            is_importance_opened: false,
        }));
    };

    const undoNoShow = async (): Promise<void> => {
        if (!appointment) return;

        setAppointmentLoadingOverlay(true);
        const [success, message] = await updateAppointment(
            appointment,
            { appointment: { is_no_show: false } },
            'Fehler beim Aktualisieren der No-Show' // Error updating no show
        );
        if (success) {
            // Update the appointment in the store with new no_show
            usePatientModalStore.setState((state) => ({
                appointment: state.appointment ? { ...state.appointment, is_no_show: false } : undefined,
            }));
            await mutatePatientAppointments();
        } else {
            showErrorNotification(message);
        }

        setAppointmentLoadingOverlay(false);
    };

    const { cancel: cancelAppointmentAction } = useCancelAppointment();

    const handleCancel = async (
        appointment: Appointment,
        scope: CollectionActionScope | undefined,
        cancelOption: CancelAppointmentOptions
    ): Promise<void> => {
        if (!appointment) return;

        setAppointmentLoadingOverlay(true);

        const cancelSuccess = await cancelAppointmentAction(appointment, scope, cancelOption);
        if (cancelSuccess) {
            await refreshAppointment(appointment);
            await mutatePatientAppointments();
            revalidateAppointmentsRangeCache();
        }

        setAppointmentLoadingOverlay(false);
    };

    const handleAppointmentClick = (clickedAppointment: DailyAppointment): void => {
        usePatientModalStore.setState({ appointment: clickedAppointment });
        // Scroll to top when new appointment is selected
        if (modalContentRef.current) {
            modalContentRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    const handleCardRead = (): void => {
        openCardReadModal({
            expectedPvsUserId: pvsPatientId,
            instanceId: instanceId,
            customerId: customerId,
            onSuccess: () => {
                if (mutateAppointmentsForDate) void mutateAppointmentsForDate();
            },
        });
    };

    return (
        <div ref={modalContentRef} className="flex flex-col gap-6">
            <PatientModalHeader
                patient={patient}
                pvsPatientId={pvsPatientId}
                instanceId={instanceId}
                isCardReadEnabled={isCardReadEnabled}
                insuranceCardReadStatus={insuranceCardReadStatus}
                onCardRead={handleCardRead}
                isTasksEnabled={isTasksEnabled}
            />
            <div className="relative flex flex-col gap-3">
                {appointmentLoadingOverlay && (
                    <div className="absolute inset-0 z-[1000] flex items-center justify-center rounded-sm bg-white/50 backdrop-blur-sm">
                        <CircularLoader />
                    </div>
                )}
                {isImportanceOpened && enrichedAppointment && (
                    <ImportanceSelector
                        value={enrichedAppointment.priority}
                        appointment={enrichedAppointment}
                        onSuccess={savePriority}
                    />
                )}
                {enrichedAppointment && (
                    <AppointmentDetails
                        appointment={enrichedAppointment}
                        onEdit={() => handleEdit(enrichedAppointment)}
                        onDelete={handleCancel}
                        undoNoShow={undoNoShow}
                        chainFutureAppointmentCount={
                            enrichedAppointment.collection_id
                                ? (chainFutureCountMap.get(enrichedAppointment.collection_id) ?? 0)
                                : 0
                        }
                    />
                )}
                <PatientModalAppointments
                    patientAppointments={patientAppointments}
                    mutatePatientAppointments={mutatePatientAppointments}
                    isLoading={isLoadingPatientAppointments}
                    patient={patient}
                    enrichedPractitionerData={enrichedPractitionerData}
                    onDelete={handleCancel}
                    onAppointmentClick={handleAppointmentClick}
                />
                <CollapsableSection>
                    <CollapsableItem title="Patienteninformation">
                        <PatientInformation patient={patient} />
                    </CollapsableItem>
                    {enrichedAppointment && (
                        <CollapsableItem title="Andere Dokumente">
                            <DocumentExchange appointment={enrichedAppointment} showDocumentsByAppointment={false} />
                        </CollapsableItem>
                    )}
                </CollapsableSection>
                {isTasksEnabled && patient && <PatientTasks patient={patient} />}
            </div>
        </div>
    );
};
